import { useEffect, useRef, useState } from 'react'

const SCROLL_CONTAINER_ID = 'mainContainer'

/** No interpolation until scroll passes this (buffer against micro-movement flicker). */
const PROGRESS_SCROLL_START_PX = 24
/** End of buffer band — full collapse. Total travel ≈ 58px. */
const PROGRESS_SCROLL_END_PX = 82

const LERP_FACTOR = 0.28
const LERP_STOP_EPS = 0.003

function clamp01(t: number): number {
  return Math.min(1, Math.max(0, t))
}

function smoothstep(t: number): number {
  const x = clamp01(t)
  return x * x * (3 - 2 * x)
}

function computeRawProgress(scrollTop: number): number {
  if (scrollTop <= PROGRESS_SCROLL_START_PX) return 0
  if (scrollTop >= PROGRESS_SCROLL_END_PX) return 1
  const t = (scrollTop - PROGRESS_SCROLL_START_PX) / (PROGRESS_SCROLL_END_PX - PROGRESS_SCROLL_START_PX)
  return smoothstep(t)
}

/**
 * Scroll progress 0→1 for `#mainContainer`, with end buffers and rAF-coalesced reads.
 * Optional lerp eases toward target (ease-out feel) when `useLerp` is true.
 */
export function useMainScrollHeaderCollapse(enabled: boolean, useLerp: boolean): number {
  const [progress, setProgress] = useState(0)
  const targetRef = useRef(0)
  const visualRef = useRef(0)
  const scrollRafRef = useRef(0)
  const lerpRafRef = useRef(0)

  useEffect(() => {
    if (!enabled) {
      if (scrollRafRef.current) cancelAnimationFrame(scrollRafRef.current)
      if (lerpRafRef.current) cancelAnimationFrame(lerpRafRef.current)
      scrollRafRef.current = 0
      lerpRafRef.current = 0
      targetRef.current = 0
      visualRef.current = 0
      setProgress(0)
      return
    }

    const el = document.getElementById(SCROLL_CONTAINER_ID)
    if (!el) return

    const applyImmediate = (scrollTop: number) => {
      const t = computeRawProgress(scrollTop)
      targetRef.current = t
      visualRef.current = t
      setProgress(t)
    }

    const tickLerp = () => {
      lerpRafRef.current = 0
      const target = targetRef.current
      let v = visualRef.current
      v += (target - v) * LERP_FACTOR
      if (Math.abs(target - v) < LERP_STOP_EPS) {
        v = target
      }
      visualRef.current = v
      setProgress(v)
      if (Math.abs(target - v) > LERP_STOP_EPS) {
        lerpRafRef.current = requestAnimationFrame(tickLerp)
      }
    }

    const onScroll = () => {
      if (scrollRafRef.current) return
      scrollRafRef.current = requestAnimationFrame(() => {
        scrollRafRef.current = 0
        const t = computeRawProgress(el.scrollTop)
        targetRef.current = t
        if (useLerp) {
          if (!lerpRafRef.current) {
            lerpRafRef.current = requestAnimationFrame(tickLerp)
          }
        } else {
          visualRef.current = t
          setProgress(t)
        }
      })
    }

    applyImmediate(el.scrollTop)
    el.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      if (scrollRafRef.current) cancelAnimationFrame(scrollRafRef.current)
      if (lerpRafRef.current) cancelAnimationFrame(lerpRafRef.current)
      el.removeEventListener('scroll', onScroll)
    }
  }, [enabled, useLerp])

  return progress
}
